import {
    AttachedFiles,
    type AttachedFilesRequest,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import moment from "moment";
import { type Moment } from "moment-recur-ts";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { DocumentInstanceType } from "types/enum";

import { getSelected } from "utilities/form/form";

import { ContactInfo } from "commonComponents/utilities/ContactInfoDisplay/ContactInfoDisplay.types";

import {
    type ILienWaiverPaymentContext,
    LienWaiverPayoutMethod,
} from "entity/bill/LienWaiverReview/LienWaiverReview.api.types";
import {
    type ILienWaiverTemplateVariables,
    parseLienWaiverStampedVariables,
} from "entity/lienWaiver/LienWaiverEditor/lienWaiverTemplateVariables";
import { type POPaymentStatus } from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";

import builderApproveIcon from "images/Common/status/BuilderApprove.png";
import declinedItemIcon from "images/Common/status/declinedItem.png";
import pendingIcon from "images/Common/status/pending.png";
import subApproveIcon from "images/Common/status/SubApprove.png";
import unreleaseIcon from "images/Common/status/unrelease.png";
import voidIcon from "images/POs/void.png";

import { type default as LienWaiverJson } from "./LienWaiver.api.json";

export type LienWaiverApiData = typeof LienWaiverJson;

export type LienWaiverFormActions =
    | "applyLienWaiver"
    | "approveLienWaiver"
    | "deleteLienWaiver"
    | "sendLienWaiver"
    | "sendLienWaiver-Conditional"
    | "sendLienWaiver-Unconditional"
    | "sendLienWaiver-FinalUnconditional"
    | "saveLienWaiver"
    | "setLienWaiverStatus"
    | undefined;

export interface IHasLienWaiver {
    lienWaiverPurchaseOrderId?: number;
    lienWaiverBillId: number;
    jobId: number;
    status?: POPaymentStatus;
    isTemplate: boolean;
    canEdit: boolean;
    canAdd: boolean;
    lienWaiverDeclineMessage?: string;
    id: number;
    attachedLienWaiver?: LienWaiverEntity;
    attachedLienWaivers?: LienWaiverEntity[];
    lienWaiverForms?: LienWaiverFormItem[];
}

export interface ILienWaiverSignatureFields {
    name: string;
    title: string;
}

export interface ILienWaiverFormValues {
    lienWaiverId: number;
    lienWaiverFormId?: number;
    lienWaiver?: LienWaiverEntity;
    /** Every waiver on the bill; populated only under the MultipleLienWaiversOnBills flag. */
    allLienWaivers?: LienWaiverEntity[];
    lienWaiverAttachedFiles?: AttachedFilesRequest;
    status?: POPaymentStatus;
    sendLienWaiverWithPayment: boolean;
    lienWaiverType: LienWaiverType;
    lienWaiverStatus?: LienWaiverStatusTypes;
}

export enum LienWaiverType {
    Unknown = 0,
    Conditional = 1,
    Unconditional = 2,
    FinalUnconditional = 3,
}

/** Per-form type + per-type default the bill's per-type rows use to pre-select each type's default. */
export interface ILienWaiverFormExtraData {
    lienWaiverType: LienWaiverType;
    isDefault: boolean;
}

export type LienWaiverFormItem = BTSelectItem<ILienWaiverFormExtraData>;

export enum LienWaiverStatusTypes {
    Unreleased = 0,
    Released = 1,
    Approved = 2,
    Declined = 3,
    BuilderApproved = 4,
    Void = 5,
    BuilderDeclined = 6,
}

export const LienWaiverStatusImages = {
    Unreleased: unreleaseIcon,
    Pending: pendingIcon,
    SubApproved: subApproveIcon,
    BuilderApproved: builderApproveIcon,
    Declined: declinedItemIcon,
    Void: voidIcon,
} as const;

export class LienWaiverConstants {
    public static readonly CreatedPaymentId = -2;
    public static readonly MadePaymentId = -3;
    public static readonly CreatedBillId = -102;
}

interface ILienWaiverStatusInformationApi {
    acceptedByName?: string;
    acceptedByTitle?: string;
    updatedDate: moment.MomentInput;
    releasedDate?: moment.MomentInput;
    signatureUrl?: string;
    status: LienWaiverStatusTypes;
    statusText: string;
}

export class LienWaiverStatusInformation {
    constructor(data: ILienWaiverStatusInformationApi) {
        this.acceptedByName = data.acceptedByName || undefined;
        this.acceptedByTitle = data.acceptedByTitle || undefined;
        this.updatedDate = moment(data.updatedDate);
        this.releasedDate = data.releasedDate ? moment(data.releasedDate) : undefined;
        this.signatureUrl = data.signatureUrl || undefined;
        this.status = data.status;
        this.statusText = data.statusText;
    }

    acceptedByName?: string;
    acceptedByTitle?: string;
    updatedDate: Moment;
    releasedDate: Moment | undefined;
    signatureUrl?: string;
    status: LienWaiverStatusTypes;
    statusText: string;
}

interface ILienWaiverReleaseDetailsApi {
    savePdfDefault?: boolean;
    savePdfPromptTitle?: string;
    titleForPdf?: string;
}

export class LienWaiverReleaseDetails {
    constructor(data: ILienWaiverReleaseDetailsApi) {
        this.savePdfDefault = data.savePdfDefault ?? false;
        this.savePdfPromptTitle = data.savePdfPromptTitle ?? "Save Pdf";
        this.titleForPdf = data.titleForPdf ?? "Pdf";
    }
    savePdfDefault: boolean;
    savePdfPromptTitle: string;
    titleForPdf: string;
}

export class LienWaiverEntity implements IHasAttachedFiles {
    private getSelectedLienWaiverForm(data: LienWaiverApiData) {
        if (this.id === 0 && this.lienWaiverForms) {
            return getSelected(this.lienWaiverForms)!.title;
        } else {
            return data.lienWaiverFormName ?? undefined;
        }
    }

    constructor(data: LienWaiverApiData) {
        this.id = data.id;
        // For new Lien Waivers, the API returns null / undefined as the id. We are safe
        // to change this to a 0, since the rest of our code expects 0 for new
        if (this.id === null || this.id === undefined) {
            this.id = 0;
        }
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.billId = data.billId;
        this.billPaymentId = data.billPaymentId ?? undefined;
        this.status = data.status;
        this.lienWaiverType = data.lienWaiverType ?? LienWaiverType.Conditional;
        this.statusText = data.statusText ? data.statusText : "";
        this.lienWaiverText = data.lienWaiverText ? data.lienWaiverText : ""; // this field is only a placeholder for the purposes of testing the scaffolding
        this.subName = data.subName ? data.subName : undefined;
        this.jobName = data.jobName ? data.jobName : undefined;
        this.jobAddress = data.jobAddress
            ? new ContactInfo({ address: data.jobAddress })
            : undefined;
        this.lotInfo = data.lotInfo ? data.lotInfo : undefined;
        this.effectiveDateOfLienWaiver = data.effectiveDateOfLienWaiver
            ? moment(data.effectiveDateOfLienWaiver)
            : undefined;
        this.lienWaiverPaymentAmount = data.lienWaiverPaymentAmount
            ? data.lienWaiverPaymentAmount
            : undefined;
        this.lienWaiverTotalPaidOnJob = data.lienWaiverTotalPaidOnJob
            ? data.lienWaiverTotalPaidOnJob
            : undefined;
        this.attachedFiles = new AttachedFiles(data.attachedFiles);
        this.documentInstanceType = DocumentInstanceType.LienWaiver;
        this.lienWaiverForms = data.lienWaiverForms
            ? data.lienWaiverForms.map((lwf) => new BTSelectItem<ILienWaiverFormExtraData>(lwf))
            : undefined;
        this.selectedLienWaiverForm = this.getSelectedLienWaiverForm(data);
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canApproveDecline = data.canApproveDecline;
        this.isOutboundEnabled = data.isOutboundEnabled;
        if (data.statusInformation) {
            this.statusInformation = new LienWaiverStatusInformation(data.statusInformation);
        }
        if (data.releaseDetails) {
            this.releaseDetails = new LienWaiverReleaseDetails(data.releaseDetails);
        }
        this.lienWaiverDialogMsg = data.lienWaiverDialogMsg ?? undefined;
        this.canViewPrice = data.canViewPrice === undefined || data.canViewPrice;
        this.name = "Lien Waiver";
        this.declineMessage = data.declineMessage ?? undefined;
        this.hasPendingPaymentServicePayment = data.hasPendingPaymentServicePayment;
        this.isPartOfOnlineMassPayment = data.isPartOfOnlineMassPayment;
        this.nutrientSigningShareToken = data.nutrientSigningShareToken ?? undefined;
        this.subId = data.subId ?? undefined;
        this.paymentShareToken = data.paymentShareToken ?? null;
        this.payoutMethod = data.payoutMethod ?? LienWaiverPayoutMethod.SubSelects;
        this.payoutDestinationLastFour = data.payoutDestinationLastFour ?? null;
        this.paymentConditionalWaiverCount = data.paymentConditionalWaiverCount ?? 0;
        this.paymentSignedConditionalWaiverCount = data.paymentSignedConditionalWaiverCount ?? 0;

        this.stampedLienWaiverVariables = parseLienWaiverStampedVariables(
            data.stampedLienWaiverVariables,
            this.canViewPrice,
            this.effectiveDateOfLienWaiver ? this.effectiveDateOfLienWaiver.toDate() : "",
        );
    }

    status: LienWaiverStatusTypes;
    lienWaiverType: LienWaiverType;
    statusText: string;
    lienWaiverText: string;
    subName?: string;
    jobName?: string;
    jobAddress?: ContactInfo;
    lotInfo?: string;
    effectiveDateOfLienWaiver?: moment.Moment;
    lienWaiverPaymentAmount?: number;
    lienWaiverTotalPaidOnJob?: number;
    documentInstanceType: DocumentInstanceType.LienWaiver;
    name: "Lien Waiver";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number;
    builderId: number;
    purchaseOrderId?: number;
    billId: number;
    billPaymentId?: number;
    lienWaiverForms?: LienWaiverFormItem[];
    selectedLienWaiverForm?: string;
    canEdit: boolean;
    canDelete: boolean;
    canApproveDecline: boolean;
    statusInformation?: LienWaiverStatusInformation;
    releaseDetails?: LienWaiverReleaseDetails;
    isOutboundEnabled: boolean;
    lienWaiverDialogMsg?: string;
    canViewPrice: boolean;
    declineMessage?: string;
    hasPendingPaymentServicePayment?: boolean;
    isPartOfOnlineMassPayment?: boolean;
    /**
     * Share token scoped to signing this one waiver through Nutrient. Only present on external (sub) loads;
     * the page's own share token covers every waiver awaiting the sub and cannot identify which to sign.
     */
    nutrientSigningShareToken?: string;
    subId?: number;

    /** Payment behind this waiver, when known. Only the last unsigned conditional waiver carries a share token. */
    paymentShareToken: string | null;
    payoutMethod: LienWaiverPayoutMethod;
    payoutDestinationLastFour: string | null;
    paymentConditionalWaiverCount: number;
    paymentSignedConditionalWaiverCount: number;
    stampedLienWaiverVariables?: ILienWaiverTemplateVariables;

    /** The payment-navigation shape the detail page consumes, absent when no payment gates this waiver. */
    get paymentContext(): ILienWaiverPaymentContext | undefined {
        if (this.paymentConditionalWaiverCount === 0) {
            return undefined;
        }

        return {
            merchantPaymentId: 0,
            paymentAmount: this.lienWaiverPaymentAmount ?? 0,
            paymentShareToken: this.paymentShareToken,
            payoutMethod: this.payoutMethod,
            payoutDestinationLastFour: this.payoutDestinationLastFour,
            waiverCount: this.paymentConditionalWaiverCount,
            signedWaiverCount: this.paymentSignedConditionalWaiverCount,
        };
    }
}

export interface ILienWaiverStatusEntityApi {
    status: LienWaiverStatusTypes;
    statusText: string;
    pdfLink: string;
    canRefreshPdf: boolean;
}

export class LienWaiverStatusEntity {
    constructor(data: ILienWaiverStatusEntityApi) {
        this.status = data.status;
        this.statusText = data.statusText;
        this.pdfLink = data.pdfLink;
        this.canRefreshPdf = data.canRefreshPdf;
    }
    status: LienWaiverStatusTypes;
    statusText: string;
    pdfLink: string;
    canRefreshPdf: boolean;
}

export interface ILienWaiverFormDefaultsRequest {
    lwFormId: number;
    poPaymentId?: number;
    poId?: number;
    billId?: number;
    jobId?: number;
}

export interface ILienWaiverCreateOrUpdateRequest {
    savePdf?: boolean;
    pdfTitle?: string;
    isMakingPayment?: boolean;
    poPaymentId?: number;
    sendLienWaiverWithPayment?: boolean;
    lienWaiverType?: LienWaiverType;
    lienWaiverForm?: number;
    lienWaiverStatus: LienWaiverStatusTypes;
    lienWaiverText?: string;
    attachedFiles: AttachedFilesRequest;
}

interface ILienWaiverCreateOrUpdateResponseApi {
    id: number;
}

export class LienWaiverCreateOrUpdateResponse {
    constructor(data: ILienWaiverCreateOrUpdateResponseApi) {
        this.id = data.id;
    }

    id: number;
}

export class LienWaiverDeleteResponse {}

interface ILienWaiverApprovalResponseApi {
    didAutoAcceptPayment?: boolean;
    isAchEnabled: boolean;
    isAutoMailCheck: boolean;
}

export class LienWaiverApprovalResponse {
    constructor(data: ILienWaiverApprovalResponseApi) {
        this.didAutoAcceptPayment = data.didAutoAcceptPayment;
        this.isAchEnabled = data.isAchEnabled;
        this.isAutoMailCheck = data.isAutoMailCheck;
    }

    didAutoAcceptPayment?: boolean;
    isAchEnabled: boolean;
    isAutoMailCheck: boolean;
}

export interface ILienWaiverExternalRequest {
    encodedLienWaiverId: string;
    shareToken?: string;
}
